// src/apiClient.js
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
    getAccessToken,
    setAccessToken,
    clearAccessToken,
    isShareToken,
    csrfHeaders,
    getCsrfToken,
} from './authToken';

const isDevelopment = process.env.NODE_ENV === 'development';

export const API_BASE_URL = isDevelopment
    ? 'http://localhost:5000'
    : process.env.REACT_APP_API_URL || window.location.origin;

const baseURL = isDevelopment
    ? 'http://localhost:5000'
    : API_BASE_URL;

const apiClient = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Required for refreshToken cookie
});

// ================ HELPERS ================

// MGMT-0042: the access token is held in memory (src/authToken.js), never in
// localStorage. Only the httpOnly refresh cookie survives a reload, and it is
// exchanged for a fresh access token on startup by AuthContext.

const clearSession = () => {
    clearAccessToken();
    localStorage.removeItem('share_token_uuid');
};

// Refreshes now happen several times across a 12-hour shift, often over farm
// mobile data. Treating every failure as "your session is gone" would throw a
// worker back to the login screen — losing whatever form they were filling in —
// because of a tunnel or a dropped bar. So failures are split in two:
//
//   session-dead  the refresh endpoint itself answered 401/403. The cookie is
//                 genuinely expired or rejected; only this logs the user out.
//   transient     no response at all (offline), 429 (the whole farm shares one
//                 public IP, so the per-IP refresh limit is shared), or 5xx.
//                 These are retried, and never clear the session.
const SESSION_DEAD = Symbol('session-dead');
const REFRESH_RETRY_DELAYS_MS = [400, 1200];

const isSessionDead = (error) => {
    const status = error?.response?.status;
    return status === 401 || status === 403;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const redirectToLogin = () => {
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

const decodeExp = (token) => {
    if (!token || isShareToken(token)) return null;

    // Must look like a real JWT
    if (token.split('.').length !== 3) return null;

    try {
        return jwtDecode(token).exp ?? null;
    } catch (e) {
        console.warn('Failed to decode JWT for expiry check:', e);
        return null;
    }
};

// The 10-minute buffer is deliberate slack: the refresh starts while the current
// token is still usable, so a blip has ten minutes to clear before anything the
// worker does can fail.
const EXPIRY_BUFFER_SECONDS = 600;

const isTokenNearExpiry = (token) => {
    const exp = decodeExp(token);
    if (exp === null) return false;
    return exp - Date.now() / 1000 < EXPIRY_BUFFER_SECONDS;
};

const isTokenExpired = (token) => {
    const exp = decodeExp(token);
    if (exp === null) return false;
    return exp - Date.now() / 1000 <= 0;
};

// The bare exchange, retried on transient failures. Never touches session state.
const postRefresh = async () => {
    let lastError;

    for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
            // Use raw axios to completely bypass the interceptor (safest).
            // The CSRF header pairs with the csrf_token cookie — /api/auth/refresh
            // rejects the request without it (MGMT-0042). It is re-read on every
            // attempt because a successful refresh rotates it.
            const response = await axios.post(
                `${baseURL}/api/auth/refresh`,
                {},
                { withCredentials: true, headers: csrfHeaders() }
            );
            return response.data.token;
        } catch (error) {
            lastError = error;

            // A real rejection of the cookie is final — retrying cannot help.
            if (isSessionDead(error)) {
                error[SESSION_DEAD] = true;
                throw error;
            }

            const delay = REFRESH_RETRY_DELAYS_MS[attempt];
            if (delay === undefined) break;
            console.warn(`Token refresh attempt ${attempt + 1} failed, retrying:`, error.message || error);
            await sleep(delay);
        }
    }

    throw lastError;
};

// One in-flight refresh shared by every caller. A page that fires several
// requests at once would otherwise run several refreshes in parallel, and since
// /api/auth/refresh rotates the CSRF cookie, a second one landing between
// another request's cookie read and the server's compare produces a spurious
// 'csrf token mismatch' 401 — a logout caused purely by our own concurrency.
let inFlightRefresh = null;

// Exchange the refresh cookie for a new access token. `bootstrap: true` is the
// cold-start call made by AuthContext when there is no in-memory token yet —
// it must NOT redirect on failure, because a logged-out visitor on a public
// page (/blackbook, a share link) legitimately has no cookie.
export const refreshAccessToken = async ({ bootstrap = false } = {}) => {
    const currentToken = getAccessToken();

    // Block refresh for share links or no session (unless bootstrapping).
    if (!bootstrap && (!currentToken || isShareToken(currentToken))) {
        console.warn('refreshAccessToken blocked: No valid authenticated session');
        throw new Error('Cannot refresh token: No authenticated session');
    }

    // The CSRF cookie is set alongside the httpOnly refresh cookie and shares its
    // lifetime, so its absence is a reliable "no session here" signal. Checking it
    // saves every anonymous visitor to a public page (/blackbook, a share link) a
    // doomed request that the server would reject for a missing CSRF header anyway.
    if (!getCsrfToken()) {
        throw new Error('Cannot refresh token: No session cookie');
    }

    if (!inFlightRefresh) {
        inFlightRefresh = postRefresh().finally(() => {
            inFlightRefresh = null;
        });
    }

    try {
        return setAccessToken(await inFlightRefresh);
    } catch (error) {
        if (error?.[SESSION_DEAD]) {
            console.error('Token refresh rejected, session over:', error.message || error);
            clearSession();

            // A failed cold-start refresh just means "not logged in" — let the
            // caller decide. Only an expired live session forces the login page.
            if (!bootstrap) redirectToLogin();
        } else {
            // Reachability, not authorisation. The session is left intact so the
            // next request can try again once the connection comes back.
            console.warn('Token refresh unavailable, keeping session:', error.message || error);
        }
        throw error;
    }
};

// ================ REQUEST INTERCEPTOR ================

apiClient.interceptors.request.use(
    async (config) => {
        let token = getAccessToken();

        // Only process real JWT tokens
        if (token && !isShareToken(token)) {
            if (token.split('.').length !== 3) {
                // Corrupted → log out
                clearSession();
                redirectToLogin();
                return Promise.reject(new Error('Invalid token format'));
            }

            if (isTokenNearExpiry(token)) {
                try {
                    token = await refreshAccessToken();
                } catch (e) {
                    // Inside the buffer the current token is still valid, so a
                    // refresh we could not reach is no reason to fail the
                    // worker's request — send it, and refresh again next time.
                    if (isTokenExpired(token)) return Promise.reject(e);
                    console.warn('Proceeding on the current token; refresh will retry.');
                }
            }

            config.headers.Authorization = `Bearer ${token}`;
        }
        // Else: share-token or no token → send NO Authorization header (correct for share links)

        // Cookie-authenticated auth routes need the double-submit CSRF header.
        if (/\/api\/auth\/(refresh|logout)$/.test(config.url || '')) {
            config.headers = { ...config.headers, ...csrfHeaders() };
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// ================ RESPONSE INTERCEPTOR ================

apiClient.interceptors.response.use(
    (response) => {
        const newToken = response.headers['x-access-token'];
        if (newToken) {
            setAccessToken(newToken);
        }
        return response;
    },
    async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const token = getAccessToken();

        // A 401 on an ordinary route usually means the access token lapsed
        // between the expiry check and the server reading it. Refresh once and
        // replay the request rather than discarding the page: only a refresh
        // that is itself rejected ends the session, and refreshAccessToken
        // handles that redirect.
        const canRetry = status === 401
            && original
            && !original._retriedAfterRefresh
            && !/\/api\/auth\/(refresh|logout)$/.test(original.url || '')
            && token
            && !isShareToken(token);

        if (canRetry) {
            original._retriedAfterRefresh = true;
            try {
                const fresh = await refreshAccessToken();
                original.headers = { ...original.headers, Authorization: `Bearer ${fresh}` };
                return apiClient(original);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }

        if (status === 401) {
            clearSession();
            redirectToLogin();
        }

        return Promise.reject(error);
    }
);

export default apiClient;
